import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, ArrowSquareOut, PencilSimple, Trash } from "@phosphor-icons/react";
import { Markierer } from "./Markierer";
import { ohneNetz, vormerken } from "../lib/warteschlange";
import { aender, lies, schick, weg } from "../api/client";
import type { Foto, FotoZiel } from "../api/types";
import { Blur, Dialog, Meldung } from "./Bausteine";

/**
 * Fotos an einem Protokollpunkt, Mangel oder einer Begehung (SPEC §5.5).
 *
 * Auf der Baustelle zählt ein Tipp: der Knopf öffnet die Dateiauswahl mit
 * `accept="image/*"` — am Telefon bietet das Kamera UND Galerie an. `capture` setzen wir
 * bewusst nicht, das erzwänge die Kamera und schlösse die Fotos aus, die schon gemacht
 * sind. Mehrere auf einmal gehen (`multiple`), das Backend nimmt bis zu 20 je Anfrage.
 *
 * `fotos` von außen (Protokoll: kommt mit dem Eintrag) spart je Punkt eine Abfrage; ohne
 * sie lädt die Leiste selbst.
 */
export function FotoLeiste({ projektId, ziel, zielId, kann, fotos, geaendert }: {
  projektId: string;
  ziel: FotoZiel;
  zielId: string;
  kann: boolean;
  fotos?: Foto[];
  geaendert?: () => void;
}) {
  const qc = useQueryClient();
  const schluessel = ["fotos", ziel, zielId];
  const selbst = useQuery({
    queryKey: schluessel,
    queryFn: () => lies<Foto[]>(`/projekte/${projektId}/fotos?ziel=${ziel}&ziel_id=${zielId}`),
    enabled: fotos === undefined,
  });
  const liste = fotos ?? selbst.data ?? [];
  const eingabe = useRef<HTMLInputElement>(null);
  const [gross, setGross] = useState<Foto | null>(null);

  const fertig = () => {
    if (fotos === undefined) qc.invalidateQueries({ queryKey: schluessel });
    geaendert?.();
  };
  const hoch = useMutation({
    mutationFn: async (dateien: File[]) => {
      const f = new FormData();
      dateien.forEach((d) => f.append("dateien", d));
      f.append("ziel", ziel);
      f.append("ziel_id", zielId);
      try {
        return await schick<Foto[]>(`/projekte/${projektId}/fotos`, f);
      } catch (fehler) {
        // Funkloch: die Fotos bleiben im Gerät und gehen später raus (lib/warteschlange.ts).
        if (!ohneNetz(fehler)) throw fehler;
        await vormerken({ art: "foto", projektId, titel: `${dateien.length} Foto${dateien.length > 1 ? "s" : ""}`,
                          ziel, zielId, bilder: dateien, namen: dateien.map((d) => d.name) });
        return [];
      }
    },
    onSuccess: fertig,
  });

  if (!kann && liste.length === 0) return null;
  return (
    <div className="fotoleiste">
      {liste.map((f) => (
        <button key={f.id} className="foto-mini" title={f.unterschrift || "Foto"} onClick={() => setGross(f)}>
          {/* ?m= : die Vorschau wird einen Tag zwischengespeichert; nach dem Markieren
              ist es eine andere Adresse, sonst zeigte der Browser die alte Fassung. */}
          <img src={`/api/fotos/${f.id}/vorschau?m=${f.markiert_id ?? ""}`} alt={f.unterschrift || "Foto"} loading="lazy" />
        </button>
      ))}
      {kann && (
        <>
          <button className="knopf leise foto-neu" disabled={hoch.isPending} onClick={() => eingabe.current?.click()}>
            <Camera size={18} /> {hoch.isPending ? "lädt …" : "Foto"}
          </button>
          <input ref={eingabe} type="file" accept="image/*,.heic,.heif" multiple hidden
                 onChange={(e) => {
                   const d = Array.from(e.target.files ?? []);
                   e.target.value = "";            // dieselbe Datei zweimal wählen dürfen
                   if (d.length) hoch.mutate(d);
                 }} />
        </>
      )}
      <Meldung fehler={hoch.error} />
      {gross && <FotoGross foto={gross} kann={kann} zu={() => setGross(null)} geaendert={fertig} />}
    </div>
  );
}

function FotoGross({ foto, kann, zu, geaendert }: {
  foto: Foto; kann: boolean; zu: () => void; geaendert: () => void;
}) {
  const unterschrift = useMutation({
    mutationFn: (u: string) => aender<Foto>(`/fotos/${foto.id}`, { unterschrift: u }),
    onSuccess: geaendert,
  });
  const loeschen = useMutation({
    mutationFn: () => weg(`/fotos/${foto.id}`),
    onSuccess: () => { geaendert(); zu(); },
  });
  const [markieren, setMarkieren] = useState(false);
  const [stand, setStand] = useState(foto);
  if (markieren) {
    return (
      <Dialog offen zu={() => setMarkieren(false)} titel="Foto markieren" fuss={null}>
        <Markierer foto={stand} abbrechen={() => setMarkieren(false)}
                   fertig={(f) => { setStand(f); setMarkieren(false); geaendert(); }} />
      </Dialog>
    );
  }
  return (
    <Dialog offen zu={zu} titel="Foto" fuss={<>
      {kann && <button className="knopf leise gefahr" onClick={() => confirm("Dieses Foto löschen?") && loeschen.mutate()}>
        <Trash size={16} /> Löschen</button>}
      <div style={{ flex: 1 }} />
      {kann && <button className="knopf leise" onClick={() => setMarkieren(true)}><PencilSimple size={16} /> Markieren</button>}
      <a className="knopf leise" href={`/api/fotos/${foto.id}/original`} target="_blank" rel="noreferrer">
        <ArrowSquareOut size={16} /> Original</a>
      <button className="knopf" onClick={zu}>Schließen</button>
    </>}>
      <img className="foto-gross" src={`/api/fotos/${foto.id}/vorschau?m=${stand.markiert_id ?? ""}`} alt={foto.unterschrift || "Foto"} />
      {kann
        ? <label className="feld" style={{ marginTop: 10 }}><span>Bildunterschrift (steht im Protokoll)</span>
            <Blur wert={foto.unterschrift} placeholder="z. B. Fallschutzkies Nordseite" speichern={(v) => unterschrift.mutate(v)} /></label>
        : foto.unterschrift && <p>{foto.unterschrift}</p>}
      <Meldung fehler={unterschrift.error ?? loeschen.error} />
    </Dialog>
  );
}
